import type {
  SponsoredDisplayAudienceTarget,
  SponsoredDisplayCapabilities,
  SponsoredDisplayContextualTarget,
} from "@adpilot/shared";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type {
  SponsoredDisplayAudienceRecommendations,
  SponsoredDisplayBidRecommendations,
  SponsoredDisplayContextualRecommendations,
} from "@/lib/campaign-creation-api";
import { AmazonAudienceCandidates, RemarketingCandidates } from "./AudienceCandidates";
import { ContextualCategoryCandidates, ContextualProductCandidates } from "./ContextualCandidates";
import type { BidLimits } from "./money";
import { SelectedTargets } from "./SelectedTargets";
import type { SponsoredDisplayDraft, TargetRow, UpdateDraftField } from "./sponsored-display-draft";
import { TargetingBidControls } from "./TargetingBidControls";

/**
 * Two-column targeting workspace: candidates for the active targeting mode on the
 * left, the targets already added (with their bids) on the right.
 */
export function TargetingEditor({
  draft,
  capabilities,
  audience,
  contextual,
  audienceError,
  contextualError,
  audienceLoading,
  contextualLoading,
  activeBids,
  currency,
  bidLimits,
  onUpdate,
  onAddAudience,
  onAddContextual,
  onApplyBid,
}: {
  draft: SponsoredDisplayDraft;
  capabilities: SponsoredDisplayCapabilities;
  audience?: SponsoredDisplayAudienceRecommendations;
  contextual?: SponsoredDisplayContextualRecommendations;
  audienceError: boolean;
  contextualError: boolean;
  audienceLoading: boolean;
  contextualLoading: boolean;
  activeBids?: SponsoredDisplayBidRecommendations;
  currency: string;
  bidLimits: BidLimits;
  onUpdate: UpdateDraftField;
  onAddAudience: (label: string, target: SponsoredDisplayAudienceTarget) => void;
  onAddContextual: (label: string, target: SponsoredDisplayContextualTarget) => void;
  onApplyBid: (value: number | null) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const activeTargets =
    draft.targetingMode === "audience" ? draft.audienceTargets : draft.contextualTargets;
  const discoveryUnavailable =
    draft.targetingMode === "audience"
      ? audienceError || (audience && audience.capability.state !== "supported")
      : contextualError || (contextual && contextual.capability.state !== "supported");
  return (
    <div
      className="grid h-[610px] max-h-[calc(100vh-8rem)] grid-cols-2 overflow-hidden rounded-lg border border-line bg-surface"
      data-slot="sd-targeting-workspace"
    >
      <div
        className="flex min-h-0 min-w-0 flex-col border-r border-line"
        data-slot="sd-targeting-candidates"
      >
        {draft.targetingMode === "audience" ? (
          <Tabs
            className="min-h-0 flex-1 gap-0"
            value={draft.audienceTab}
            onValueChange={(value) => {
              if (value === "amazon" || value === "views" || value === "purchases") {
                onUpdate("audienceTab", value);
              }
            }}
          >
            <TabsList
              className="h-14 w-full shrink-0 justify-start px-4 pt-1"
              data-slot="sd-targeting-candidate-header"
            >
              <TabsTrigger
                value="amazon"
                disabled={capabilities.targeting.amazonAudience.state !== "supported"}
              >
                {t("sd.audience.tabs.amazon")}
              </TabsTrigger>
              <TabsTrigger
                value="views"
                disabled={capabilities.targeting.viewsRemarketing.state !== "supported"}
              >
                {t("sd.audience.tabs.views")}
              </TabsTrigger>
              <TabsTrigger
                value="purchases"
                disabled={capabilities.targeting.purchasesRemarketing.state !== "supported"}
              >
                {t("sd.audience.tabs.purchases")}
              </TabsTrigger>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger render={<span className="inline-flex" />}>
                    <TabsTrigger
                      value="amc"
                      disabled
                      aria-label={`${t("sd.audience.tabs.amc")}: ${t("sd.unsupported")}`}
                    >
                      {t("sd.audience.tabs.amc")}
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent>{t("sd.unsupported")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TabsList>
            <TargetingBidControls
              bidMode={draft.bidMode}
              currency={currency}
              customBid={draft.customBid}
              bidLimits={bidLimits}
              onApplyBid={onApplyBid}
              onUpdate={onUpdate}
            />
            {discoveryUnavailable ? <DiscoveryUnavailableAlert /> : null}
            <TabsContent value="amazon" className="min-h-0 overflow-hidden">
              <AmazonAudienceCandidates
                data={audience}
                loading={audienceLoading}
                search={draft.audienceSearch}
                onSearchChange={(value) => onUpdate("audienceSearch", value)}
                onAdd={onAddAudience}
              />
            </TabsContent>
            <TabsContent value="views" className="min-h-0 overflow-hidden">
              <RemarketingCandidates
                categories={contextual?.categories.items ?? []}
                mode="views"
                onAdd={onAddAudience}
              />
            </TabsContent>
            <TabsContent value="purchases" className="min-h-0 overflow-hidden">
              <RemarketingCandidates
                categories={contextual?.categories.items ?? []}
                mode="purchases"
                onAdd={onAddAudience}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs
            className="min-h-0 flex-1 gap-0"
            value={draft.contextualTab}
            onValueChange={(value) => {
              if (value === "categories" || value === "products") onUpdate("contextualTab", value);
            }}
          >
            <TabsList
              className="h-14 w-full shrink-0 justify-start px-4 pt-1"
              data-slot="sd-targeting-candidate-header"
            >
              <TabsTrigger value="categories">{t("sd.contextual.tabs.categories")}</TabsTrigger>
              <TabsTrigger value="products">{t("sd.contextual.tabs.products")}</TabsTrigger>
            </TabsList>
            <TargetingBidControls
              bidMode={draft.bidMode}
              currency={currency}
              customBid={draft.customBid}
              bidLimits={bidLimits}
              onApplyBid={onApplyBid}
              onUpdate={onUpdate}
            />
            {discoveryUnavailable ? <DiscoveryUnavailableAlert /> : null}
            <TabsContent value="categories" className="min-h-0 overflow-hidden">
              <ContextualCategoryCandidates
                data={contextual}
                loading={contextualLoading}
                onAdd={onAddContextual}
              />
            </TabsContent>
            <TabsContent value="products" className="min-h-0 overflow-hidden">
              <ContextualProductCandidates
                data={contextual}
                input={draft.contextualAsinInput}
                loading={contextualLoading}
                onInputChange={(value) => onUpdate("contextualAsinInput", value)}
                onAdd={onAddContextual}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
      <SelectedTargets
        bids={activeBids}
        currency={currency}
        bidLimits={bidLimits}
        mode={draft.targetingMode}
        rows={activeTargets}
        onRowsChange={(rows) => {
          if (draft.targetingMode === "audience") {
            onUpdate("audienceTargets", rows as Array<TargetRow<SponsoredDisplayAudienceTarget>>);
          } else {
            onUpdate(
              "contextualTargets",
              rows as Array<TargetRow<SponsoredDisplayContextualTarget>>,
            );
          }
        }}
      />
    </div>
  );
}

function DiscoveryUnavailableAlert() {
  const { t } = useTranslation("campaignCreation");
  return (
    <Alert className="mx-4 mt-3 w-auto shrink-0" variant="warning">
      <IconAlertTriangle />
      <AlertTitle>{t("sd.targeting.recommendationsUnavailable")}</AlertTitle>
      <AlertDescription>{t("sd.targeting.customStillAvailable")}</AlertDescription>
    </Alert>
  );
}
